/**
 * TanStack Query hooks of universal databases: the only API access of the module.
 *
 * Records are paged (`useRecords`, an infinite query per table and query); a cell edit updates
 * every loaded page of its table optimistically and rolls back on error. The server checks every
 * permission; `my_permissions` only decides what the UI offers.
 */
import {
  type InfiniteData,
  keepPreviousData,
  type QueryClient,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { api, isApiError, queryKeys } from '@/shared/api';

import { applyCells } from './cells';
import { mergeStyle } from './style';
import type {
  CellValue,
  ColumnConfig,
  Database,
  CopyReport,
  DatabaseAccess,
  DatabaseColumn,
  DatabaseRecord,
  DatabaseRole,
  DatabaseAppearance,
  HeaderStyle,
  StylePatch,
  DuplicateDatabaseInput,
  DuplicateTableInput,
  DatabaseTable,
  FieldType,
  GroupCounts,
  OwnerOut,
  RecordFilter,
  RecordSearchPage,
  RecordPage,
  TrashedRecordPage,
  RecordQuery,
  RuleInput,
  TableDetail,
} from './types';

const RECORD_PAGE_SIZE = 100;

// --- databases -------------------------------------------------------------------------------

export function useMyDatabases(enabled = true) {
  return useQuery({
    queryKey: queryKeys.databases.mine,
    queryFn: ({ signal }) => api.get<Database[]>('/v1/databases', { signal }),
    enabled,
  });
}

export function useWorkspaceDatabases(workspaceId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.databases.list(workspaceId),
    queryFn: ({ signal }) =>
      api.get<Database[]>(`/v1/workspaces/${workspaceId}/databases`, { signal }),
    enabled: enabled && workspaceId !== '',
  });
}

export function useDatabase(databaseId: string) {
  return useQuery({
    queryKey: queryKeys.databases.detail(databaseId),
    queryFn: ({ signal }) => api.get<Database>(`/v1/databases/${databaseId}`, { signal }),
  });
}

function storeDatabase(client: QueryClient, database: Database) {
  client.setQueryData(queryKeys.databases.detail(database.id), database);
  void client.invalidateQueries({ queryKey: queryKeys.databases.mine });
  void client.invalidateQueries({ queryKey: queryKeys.databases.list(database.workspace_id) });
}

export function useCreateDatabase() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      workspaceId,
      ...body
    }: {
      workspaceId: string;
      name: string;
      description?: string;
      is_confidential: boolean;
      section_id?: string;
      group_id?: string;
      /** Access chosen at creation (team workspaces; same rules as adding them later). */
      team_access?: { team_id: string; role: string }[];
      role_access?: { subject_role?: string; custom_role_id?: string; role: string }[];
    }) => api.post<Database>(`/v1/workspaces/${workspaceId}/databases`, body),
    onSuccess: (database) => {
      storeDatabase(client, database);
    },
  });
}

/** A copy of a database (structure, optionally records); the copy is owned by the caller. */
export function useDuplicateDatabase(sourceId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: DuplicateDatabaseInput) =>
      api.post<{ database: Database; report: CopyReport }>(
        `/v1/databases/${sourceId}/duplicate`,
        input,
      ),
    onSuccess: ({ database }) => {
      storeDatabase(client, database);
    },
  });
}

export interface DatabasePatch {
  name?: string;
  description?: string | null;
  is_confidential?: boolean;
  /** An identification color from the fixed palette; `null` clears it. */
  color?: string | null;
  /** The catalog card's look; `null` = the standard card. */
  appearance?: DatabaseAppearance | null;
  expected_version?: number;
}

/** The workspace labels shown on the database's card: the full list, in display order. */
export function useSetDatabaseLabels(databaseId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (labelIds: string[]) =>
      api.put<Database>(`/v1/databases/${databaseId}/labels`, { label_ids: labelIds }),
    onSuccess: (database) => {
      storeDatabase(client, database);
    },
  });
}

export function useUpdateDatabase(databaseId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (patch: DatabasePatch) => api.patch<Database>(`/v1/databases/${databaseId}`, patch),
    onSuccess: (database) => {
      storeDatabase(client, database);
      void client.invalidateQueries({ queryKey: queryKeys.databases.access(databaseId) });
    },
    onError: (error) => {
      if (isApiError(error) && error.code === 'VERSION_CONFLICT') {
        void client.invalidateQueries({ queryKey: queryKeys.databases.detail(databaseId) });
      }
    },
  });
}

export function useMoveDatabase(workspaceId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...anchor }: { id: string; after_id?: string; before_id?: string }) =>
      api.post<Database>(`/v1/databases/${id}/move`, anchor),
    onSettled: () => {
      void client.invalidateQueries({ queryKey: queryKeys.databases.list(workspaceId) });
      void client.invalidateQueries({ queryKey: queryKeys.databases.mine });
    },
  });
}

/** The viewer's own favorite mark: personal, so only their catalogs are refreshed. */
export function useToggleDatabaseFavorite(database: Pick<Database, 'id' | 'workspace_id'>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (favorite: boolean) =>
      favorite
        ? api.put(`/v1/databases/${database.id}/favorite`)
        : api.delete(`/v1/databases/${database.id}/favorite`),
    onSettled: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: queryKeys.databases.list(database.workspace_id) }),
        client.invalidateQueries({ queryKey: queryKeys.databases.mine }),
        client.invalidateQueries({ queryKey: queryKeys.databases.detail(database.id) }),
      ]);
    },
  });
}

export function useDeleteDatabase() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (database: Pick<Database, 'id' | 'workspace_id'>) =>
      api.delete(`/v1/databases/${database.id}`),
    onSuccess: (_, database) => {
      client.removeQueries({ queryKey: queryKeys.databases.detail(database.id) });
      void client.invalidateQueries({ queryKey: queryKeys.databases.list(database.workspace_id) });
      void client.invalidateQueries({ queryKey: queryKeys.databases.mine });
    },
  });
}

export function useTransferDatabaseOwner(databaseId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: { user_id: string; expected_version?: number }) =>
      api.put<OwnerOut>(`/v1/databases/${databaseId}/owner`, body),
    onSettled: () => {
      // The actor's own access may be gone: everything about this database is re-read.
      void client.invalidateQueries({ queryKey: queryKeys.databases.detail(databaseId) });
      void client.invalidateQueries({ queryKey: queryKeys.databases.access(databaseId) });
      void client.invalidateQueries({ queryKey: queryKeys.databases.mine });
    },
  });
}

// --- tables ----------------------------------------------------------------------------------

export function useTables(databaseId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.databases.tables(databaseId),
    queryFn: ({ signal }) =>
      api.get<DatabaseTable[]>(`/v1/databases/${databaseId}/tables`, { signal }),
    enabled,
  });
}

export interface TableNameHit {
  id: string;
  name: string;
  database_id: string;
  database_name: string;
}

/** Tables by name (at most 20) in databases the viewer may open - for quick navigation. */
export function useTableNameSearch(text: string) {
  const q = text.trim();
  return useQuery({
    queryKey: ['database-table-search', q] as const,
    queryFn: ({ signal }) =>
      api.get<TableNameHit[]>('/v1/database-table-search', { signal, query: { q } }),
    enabled: q.length >= 2,
    staleTime: 30_000,
  });
}

export function useTable(tableId: string | null) {
  return useQuery({
    queryKey: queryKeys.databases.table(tableId ?? ''),
    queryFn: ({ signal }) => api.get<TableDetail>(`/v1/database-tables/${tableId}`, { signal }),
    enabled: tableId !== null,
  });
}

function useInvalidateTables(databaseId: string) {
  const client = useQueryClient();
  return () => client.invalidateQueries({ queryKey: queryKeys.databases.tables(databaseId) });
}

export function useCreateTable(databaseId: string) {
  const invalidate = useInvalidateTables(databaseId);
  return useMutation({
    mutationFn: (name: string) =>
      api.post<DatabaseTable>(`/v1/databases/${databaseId}/tables`, { name }),
    onSuccess: () => invalidate(),
  });
}

/** A copy of a table in its own database or another one (`input.database_id`). */
export function useDuplicateTable(tableId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: DuplicateTableInput) =>
      api.post<{ table: DatabaseTable; report: CopyReport }>(
        `/v1/database-tables/${tableId}/duplicate`,
        input,
      ),
    onSuccess: ({ table }) =>
      client.invalidateQueries({ queryKey: queryKeys.databases.tables(table.database_id) }),
  });
}

/** Rename a table and/or set its identification color (`null` clears it). */
export function useRenameTable(databaseId: string) {
  const client = useQueryClient();
  const invalidate = useInvalidateTables(databaseId);
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: string; name?: string; color?: string | null }) =>
      api.patch<DatabaseTable>(`/v1/database-tables/${id}`, patch),
    onSuccess: (table) => {
      void client.invalidateQueries({ queryKey: queryKeys.databases.table(table.id) });
      return invalidate();
    },
  });
}

export function useMoveTable(databaseId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...anchor }: { id: string; after_id?: string; before_id?: string }) =>
      api.post<DatabaseTable>(`/v1/database-tables/${id}/move`, anchor),
    onMutate: async ({ id, after_id, before_id }) => {
      const key = queryKeys.databases.tables(databaseId);
      await client.cancelQueries({ queryKey: key });
      const previous = client.getQueryData<DatabaseTable[]>(key);
      if (previous) {
        const moving = previous.find((t) => t.id === id);
        const rest = previous.filter((t) => t.id !== id);
        if (moving) {
          const anchor = after_id ?? before_id;
          const at = anchor ? rest.findIndex((t) => t.id === anchor) : rest.length - 1;
          rest.splice(after_id ? at + 1 : Math.max(at, 0), 0, moving);
          client.setQueryData(key, rest);
        }
      }
      return { previous };
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) {
        client.setQueryData(queryKeys.databases.tables(databaseId), context.previous);
      }
    },
    onSettled: () => client.invalidateQueries({ queryKey: queryKeys.databases.tables(databaseId) }),
  });
}

export function useDeleteTable(databaseId: string) {
  const invalidate = useInvalidateTables(databaseId);
  return useMutation({
    mutationFn: (tableId: string) => api.delete(`/v1/database-tables/${tableId}`),
    onSuccess: () => invalidate(),
  });
}

// --- columns ---------------------------------------------------------------------------------

function useInvalidateTable(tableId: string) {
  const client = useQueryClient();
  return async () => {
    await client.invalidateQueries({ queryKey: queryKeys.databases.table(tableId) });
    await client.invalidateQueries({ queryKey: queryKeys.databases.recordsAll(tableId) });
  };
}

export interface ColumnInput {
  name: string;
  field_type: FieldType;
  config?: ColumnConfig;
  wrap_text?: boolean;
  after_id?: string;
  before_id?: string;
}

export function useCreateColumn(tableId: string) {
  const invalidate = useInvalidateTable(tableId);
  return useMutation({
    mutationFn: (input: ColumnInput) =>
      api.post<DatabaseColumn>(`/v1/database-tables/${tableId}/columns`, input),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateColumn(tableId: string) {
  const invalidate = useInvalidateTable(tableId);
  const client = useQueryClient();
  return useMutation({
    mutationKey: ['databases', 'update-column', tableId],
    scope: { id: `database-columns-${tableId}` },
    mutationFn: ({
      id,
      ...patch
    }: {
      id: string;
      name?: string;
      field_type?: FieldType;
      config?: ColumnConfig;
      wrap_text?: boolean;
      frozen?: boolean;
      /** A palette color; `null` clears it. */
      color?: string | null;
      /** The background the editor saw; another stored color refuses the change (a conflict). */
      expected_color?: string | null;
      /** Header appearance, a partial patch (`null` = inherit again). */
      header_style?: StylePatch<HeaderStyle>;
      /** The stored header properties the editor saw; a different one among those changed is a conflict. */
      expected_header_style?: StylePatch<HeaderStyle>;
      expected_version?: number;
    }) => api.patch<DatabaseColumn>(`/v1/database-columns/${id}`, patch),
    // A header change is shown at once (so the next click expects what this one stored) and is
    // restored when refused; changes are sent one after another, never racing each other.
    onMutate: ({ id, header_style }) => {
      if (!header_style) return undefined;
      const key = queryKeys.databases.table(tableId);
      const previous = client.getQueryData<TableDetail>(key);
      if (previous) {
        client.setQueryData<TableDetail>(key, {
          ...previous,
          columns: previous.columns.map((column) =>
            column.id === id
              ? { ...column, header_style: mergeStyle(column.header_style, header_style) ?? {} }
              : column,
          ),
        });
      }
      return { previous };
    },
    onError: (_error, _input, context) => {
      if (context?.previous) {
        client.setQueryData(queryKeys.databases.table(tableId), context.previous);
      }
    },
    onSettled: () => invalidate(),
  });
}

export function useMoveColumn(tableId: string) {
  const invalidate = useInvalidateTable(tableId);
  return useMutation({
    mutationFn: ({ id, ...anchor }: { id: string; after_id?: string; before_id?: string }) =>
      api.post<DatabaseColumn>(`/v1/database-columns/${id}/move`, anchor),
    onSettled: () => invalidate(),
  });
}

export function useDeleteColumn(tableId: string) {
  const invalidate = useInvalidateTable(tableId);
  return useMutation({
    mutationFn: (columnId: string) => api.delete(`/v1/database-columns/${columnId}`),
    onSuccess: () => invalidate(),
  });
}

// --- records ---------------------------------------------------------------------------------

/** Query parameters of `GET /database-tables/{id}/records` (see `databases/query.py`). */
export function recordParams(query: RecordQuery): {
  query: Record<string, string>;
  repeat: Record<string, string[]>;
} {
  const params: Record<string, string> = {};
  if (query.q.trim()) params.q = query.q.trim();
  const [only] = query.sort;
  if (query.sort.length === 1 && only) {
    params.sort = only.column_id;
    params.direction = only.direction;
  }
  const repeat: Record<string, string[]> = {};
  const filters = query.filters.map((f) =>
    f.value === undefined ? `${f.column_id}:${f.op}` : `${f.column_id}:${f.op}:${f.value}`,
  );
  if (filters.length > 0) repeat.filter = filters;
  // Several levels, most significant first (`order=<column_id>:asc|desc`, repeated).
  if (query.sort.length > 1) repeat.order = query.sort.map((s) => `${s.column_id}:${s.direction}`);
  return { query: params, repeat };
}

export function useRecords(tableId: string, query: RecordQuery, enabled = true) {
  return useInfiniteQuery({
    enabled,
    queryKey: queryKeys.databases.records(tableId, query),
    queryFn: ({ pageParam, signal }) => {
      const { query: params, repeat } = recordParams(query);
      return api.get<RecordPage>(`/v1/database-tables/${tableId}/records`, {
        signal,
        query: { ...params, limit: RECORD_PAGE_SIZE, cursor: pageParam },
        repeat,
      });
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    // A new filter / sort keeps showing the rows it replaces until the answer arrives: the grid (and
    // the column menu being used) stays put instead of being swapped for a loading state.
    placeholderData: keepPreviousData,
  });
}

/** Cards per Kanban column per request (more come with "Load more"). */
const BOARD_PAGE_SIZE = 20;

/** Board (Kanban) queries live under the table's records key, so every create / delete / move and
 * every realtime `records` hint refreshes them with the grid; marker: key[3] === 'board'. */
const boardKey = (tableId: string, ...rest: unknown[]) =>
  [...queryKeys.databases.recordsAll(tableId), 'board', ...rest] as const;

/** Per-option counts of a Kanban column set, for the same search / filters as the cards. */
export function useGroupCounts(
  tableId: string,
  columnId: string | null,
  query: RecordQuery,
  enabled = true,
) {
  const { q, filters } = query;
  return useQuery({
    enabled: enabled && columnId !== null,
    queryKey: boardKey(tableId, 'counts', columnId, q, filters),
    queryFn: ({ signal }) => {
      const { query: params, repeat } = recordParams({ q, filters, sort: [] });
      return api.get<GroupCounts>(`/v1/database-tables/${tableId}/groups`, {
        signal,
        query: { ...params, column: columnId ?? '' },
        repeat,
      });
    },
    placeholderData: keepPreviousData,
  });
}

/**
 * The cards of ONE Kanban group: the ordinary records query plus the group's own condition
 * (`in` / `has_any` / `empty` on the grouping column), so filters, search, sorting and the
 * tie-break are the table's - and paging, caching and optimistic cell edits are too.
 */
export function useGroupCards(
  tableId: string,
  query: RecordQuery,
  group: RecordFilter,
  enabled = true,
) {
  const full: RecordQuery = { ...query, filters: [...query.filters, group] };
  return useInfiniteQuery({
    enabled,
    queryKey: boardKey(tableId, 'cards', full),
    queryFn: ({ pageParam, signal }) => {
      const { query: params, repeat } = recordParams(full);
      return api.get<RecordPage>(`/v1/database-tables/${tableId}/records`, {
        signal,
        query: { ...params, limit: BOARD_PAGE_SIZE, cursor: pageParam },
        repeat,
      });
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    placeholderData: keepPreviousData,
  });
}

/** One record by id (the Kanban board has no flat list to find an open record in). */
export function useRecordById(tableId: string, id: string | null, enabled = true) {
  return useQuery({
    enabled: enabled && id !== null,
    queryKey: boardKey(tableId, 'one', id),
    queryFn: ({ signal }) =>
      api.get<DatabaseRecord>(`/v1/database-records/${id ?? ''}`, { signal }),
  });
}

/** A card moved or a record changed its group: counts and every column are read again. */
export function refreshBoards(client: QueryClient, tableId: string): Promise<void> {
  return client.invalidateQueries({
    queryKey: queryKeys.databases.recordsAll(tableId),
    predicate: (query) => query.queryKey[3] === 'board',
  });
}

type Pages = InfiniteData<RecordPage, string | null>;

/** Apply `change` to every loaded page of the table (all queries / filters). */
function editPages(
  client: QueryClient,
  tableId: string,
  change: (items: DatabaseRecord[]) => DatabaseRecord[],
) {
  client.setQueriesData<Pages>({ queryKey: queryKeys.databases.recordsAll(tableId) }, (data) =>
    data && 'pages' in data
      ? { ...data, pages: data.pages.map((page) => ({ ...page, items: change(page.items) })) }
      : data,
  );
}

export function replaceRecord(client: QueryClient, record: DatabaseRecord) {
  editPages(client, record.table_id, (items) =>
    items.map((item) => (item.id === record.id ? record : item)),
  );
}

export function useCreateRecord(tableId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      cells: Record<string, unknown>;
      after_id?: string;
      /** Create the record inside this record section (none = "No section"). */
      section_id?: string | null;
    }) => api.post<DatabaseRecord>(`/v1/database-tables/${tableId}/records`, body),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: queryKeys.databases.recordsAll(tableId) }),
  });
}

export interface CellEdit {
  record: DatabaseRecord;
  cells: Record<string, unknown>;
  /**
   * The values the editor saw when editing began (`null` = was empty), per written cell; a cell
   * the server holds differently is refused (409, `DATABASE_EDIT_CONFLICT`). Default: the cells
   * of `record` - the state the edit was made on, NEVER the newest cached one.
   */
  seen?: Record<string, CellValue | null>;
  /** The user's explicit retry after a conflict (diagnostics only; the check still runs). */
  afterConflict?: boolean;
}

/** A refused edit: what the server holds NOW for the cells that changed under the editor. */
export interface EditConflict {
  recordId: string;
  version: number;
  /** `column_id` is set for a cell value / cell format, absent for the row background. */
  fields: { columnId: string | null; property: string | null; current: CellValue | null }[];
}

/** The details of a `DATABASE_EDIT_CONFLICT` answer, or `null` for any other error. */
export function editConflict(error: unknown): EditConflict | null {
  if (!isApiError(error) || error.code !== 'DATABASE_EDIT_CONFLICT') return null;
  const details = error.details as {
    record_id?: string;
    current_version?: number;
    conflicts?: { column_id?: string; property?: string; current?: CellValue | null }[];
  } | null;
  return {
    recordId: details?.record_id ?? '',
    version: details?.current_version ?? 0,
    fields: (details?.conflicts ?? []).map((c) => ({
      columnId: c.column_id ?? null,
      property: c.property ?? null,
      current: c.current ?? null,
    })),
  };
}

/** The record as currently cached (the latest saved version, with optimistic cells). */
function cachedRecord(
  client: QueryClient,
  tableId: string,
  id: string,
): DatabaseRecord | undefined {
  for (const [, data] of client.getQueriesData<Pages>({
    queryKey: queryKeys.databases.recordsAll(tableId),
  })) {
    for (const page of data?.pages ?? []) {
      const found = page.items.find((item) => item.id === id);
      if (found) return found;
    }
  }
  return undefined;
}

function withCells(record: DatabaseRecord, cells: Record<string, unknown>): DatabaseRecord {
  return { ...record, cells: applyCells(record.cells, cells) };
}

/**
 * One record's cells, optimistically: the new values show at once and the server answer (with
 * the new version) replaces them; an error restores the cells. Every written cell is sent with
 * the value the editor SAW (`seen`): the server refuses the write when that cell was changed by
 * somebody else meanwhile (409 `DATABASE_EDIT_CONFLICT`, nothing written) - other cells of the
 * record never conflict. The record's version is not used, so two people editing different
 * cells never block each other. Edits of a table run one after another (`scope`). A conflict
 * is reported to the caller (`editConflict`), which keeps the user's draft; the cache gets the
 * server's current value of the conflicting cells without another request.
 */
export function useUpdateRecord(tableId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationKey: ['databases', 'update-record', tableId],
    scope: { id: `database-records-${tableId}` },
    mutationFn: ({ record, cells, seen, afterConflict }: CellEdit) =>
      api.patch<DatabaseRecord>(`/v1/database-records/${record.id}`, {
        cells,
        expected_cells:
          seen ??
          Object.fromEntries(Object.keys(cells).map((key) => [key, record.cells[key] ?? null])),
        ...(afterConflict && { after_conflict: true }),
      }),
    onMutate: async ({ record, cells }) => {
      await client.cancelQueries({ queryKey: queryKeys.databases.recordsAll(tableId) });
      const current = cachedRecord(client, tableId, record.id) ?? record;
      const previous = Object.fromEntries(
        Object.keys(cells).map((key) => [key, current.cells[key] ?? null]),
      );
      replaceRecord(client, withCells(current, cells));
      return { previous };
    },
    onSuccess: (saved) => {
      replaceRecord(client, saved);
      // a changed grouping cell moves a card to another Kanban column and changes the counts
      void refreshBoards(client, tableId);
    },
    onError: async (error, { record }, context) => {
      const current = cachedRecord(client, tableId, record.id);
      if (current && context?.previous) replaceRecord(client, withCells(current, context.previous));
      const conflict = editConflict(error);
      if (conflict) {
        const fresh = cachedRecord(client, tableId, record.id);
        if (fresh) {
          const known = Object.fromEntries(
            conflict.fields.filter((f) => f.columnId).map((f) => [f.columnId ?? '', f.current]),
          );
          replaceRecord(client, { ...withCells(fresh, known), version: conflict.version });
        }
        void refreshBoards(client, tableId);
      } else if (isApiError(error) && error.code === 'VERSION_CONFLICT') {
        try {
          replaceRecord(client, await api.get<DatabaseRecord>(`/v1/database-records/${record.id}`));
        } catch {
          void client.invalidateQueries({ queryKey: queryKeys.databases.recordsAll(tableId) });
        }
      }
    },
  });
}

/** `values` with `columnIds` set to `value`, or removed when `value` is `null`. */
function withCellValue<T>(
  values: Record<string, T>,
  columnIds: readonly string[],
  value: T | null,
): Record<string, T> {
  const next = Object.fromEntries(
    Object.entries(values).filter(([key]) => !columnIds.includes(key)),
  );
  if (value !== null) for (const columnId of columnIds) next[columnId] = value;
  return next;
}

export interface FormatInput {
  record_ids: string[];
  /** Absent: the whole rows; present: those cells. */
  column_ids?: string[];
  /** A palette color; `null` clears it; absent leaves the background alone. */
  color?: string | null;
  /** Wrapping of those cells (needs `column_ids`); `null` hands them back to their column. */
  wrap?: boolean | null;
  /**
   * What the editor saw (`null` = nothing stored): refused with a conflict when the stored
   * background / wrapping differs. Omitted = unconditional (bulk painting).
   */
  expected_color?: string | null;
  expected_wrap?: boolean | null;
  /**
   * Text formatting of those cells (needs `column_ids`), a PARTIAL patch: a named property is set,
   * `null` removes it, an unnamed one is left alone - so changing one property can never overwrite
   * another that somebody else changed.
   */
  style?: StylePatch;
  /** The stored values of the properties the editor saw (`null` = none); a different one is a conflict. */
  expected_style?: StylePatch;
}

/** Row / cell formatting (background, wrapping), shown at once and restored when refused. */
export function useFormatRecords(tableId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: FormatInput) =>
      api.post<{ updated: number }>(`/v1/database-tables/${tableId}/formatting`, input),
    onMutate: async (input) => {
      await client.cancelQueries({ queryKey: queryKeys.databases.recordsAll(tableId) });
      const snapshot = client.getQueriesData<Pages>({
        queryKey: queryKeys.databases.recordsAll(tableId),
      });
      const ids = new Set(input.record_ids);
      editPages(client, tableId, (items) =>
        items.map((record) => {
          if (!ids.has(record.id)) return record;
          let next = record;
          if (input.color !== undefined) {
            next = input.column_ids
              ? {
                  ...next,
                  cell_colors: withCellValue(next.cell_colors, input.column_ids, input.color),
                }
              : { ...next, color: input.color };
          }
          if (input.wrap !== undefined && input.column_ids) {
            next = {
              ...next,
              cell_wrap: withCellValue(next.cell_wrap, input.column_ids, input.wrap),
            };
          }
          if (input.style && input.column_ids) {
            const patch = input.style;
            const styles = new Map(Object.entries(next.cell_styles));
            for (const columnId of input.column_ids) {
              const merged = mergeStyle(styles.get(columnId), patch);
              if (merged) styles.set(columnId, merged);
              else styles.delete(columnId);
            }
            next = { ...next, cell_styles: Object.fromEntries(styles) };
          }
          return next;
        }),
      );
      return { snapshot };
    },
    onError: (_error, _input, context) => {
      for (const [key, data] of context?.snapshot ?? []) client.setQueryData(key, data);
    },
    onSettled: () =>
      client.invalidateQueries({ queryKey: queryKeys.databases.recordsAll(tableId) }),
  });
}

export function useMoveRecord(tableId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...anchor
    }: {
      id: string;
      after_id?: string;
      before_id?: string;
      /** SENT = also put the record in that section (`null` = "No section"); absent = unchanged. */
      section_id?: string | null;
    }) => api.post<DatabaseRecord>(`/v1/database-records/${id}/move`, anchor),
    onSettled: () =>
      client.invalidateQueries({ queryKey: queryKeys.databases.recordsAll(tableId) }),
  });
}

export function useDeleteRecords(tableId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (ids: readonly string[]) => {
      for (const id of ids) await api.delete(`/v1/database-records/${id}`);
    },
    onMutate: (ids) => {
      const gone = new Set(ids);
      editPages(client, tableId, (items) => items.filter((item) => !gone.has(item.id)));
    },
    onSettled: () =>
      client.invalidateQueries({ queryKey: queryKeys.databases.recordsAll(tableId) }),
  });
}

// --- the trash of a table's records -------------------------------------------------------------

/** Deleted records per request in the trash dialog. */
const TRASH_PAGE_SIZE = 25;

/** The trash lives under the table's records key: a realtime `records` hint refreshes it too. */
const trashKey = (tableId: string, q: string) =>
  [...queryKeys.databases.recordsAll(tableId), 'trash', q] as const;

export function useTrashedRecords(tableId: string, q: string, enabled = true) {
  return useInfiniteQuery({
    enabled,
    queryKey: trashKey(tableId, q.trim()),
    queryFn: ({ pageParam, signal }) =>
      api.get<TrashedRecordPage>(`/v1/database-tables/${tableId}/trash/records`, {
        signal,
        query: { ...(q.trim() && { q: q.trim() }), limit: TRASH_PAGE_SIZE, cursor: pageParam },
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    placeholderData: keepPreviousData,
  });
}

/** Bring a deleted record back: the table and the trash are read again. */
export function useRestoreRecord(tableId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<DatabaseRecord>(`/v1/database-records/${id}/restore`),
    onSettled: () =>
      client.invalidateQueries({ queryKey: queryKeys.databases.recordsAll(tableId) }),
  });
}

/** Delete a record that is in the trash for good (cannot be undone). */
export function usePurgeRecord(tableId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/v1/database-records/${id}/purge`),
    onSettled: () =>
      client.invalidateQueries({ queryKey: queryKeys.databases.recordsAll(tableId) }),
  });
}

// --- access administration -------------------------------------------------------------------

export function useDatabaseAccess(databaseId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.databases.access(databaseId),
    queryFn: ({ signal }) =>
      api.get<DatabaseAccess>(`/v1/databases/${databaseId}/access`, { signal }),
    enabled,
  });
}

/** Every access change answers with the new access view; the viewer's own rights may change. */
function useAccessMutation<V>(databaseId: string, call: (vars: V) => Promise<unknown>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: call,
    onSuccess: (result) => {
      if (result && typeof result === 'object' && 'database_id' in result) {
        client.setQueryData(queryKeys.databases.access(databaseId), result);
      }
    },
    onSettled: () => {
      void client.invalidateQueries({ queryKey: queryKeys.databases.access(databaseId) });
      void client.invalidateQueries({ queryKey: queryKeys.databases.detail(databaseId) });
    },
  });
}

const base = (databaseId: string) => `/v1/databases/${databaseId}`;

export function useSetDatabaseMember(databaseId: string) {
  return useAccessMutation(databaseId, ({ userId, role }: { userId: string; role: DatabaseRole }) =>
    api.put<DatabaseAccess>(`${base(databaseId)}/members/${userId}`, { role }),
  );
}

export function useRemoveDatabaseMember(databaseId: string) {
  return useAccessMutation(databaseId, (userId: string) =>
    api.delete(`${base(databaseId)}/members/${userId}`),
  );
}

export function useSetDatabaseDenial(databaseId: string) {
  return useAccessMutation(databaseId, ({ userId, denied }: { userId: string; denied: boolean }) =>
    denied
      ? api.put<DatabaseAccess>(`${base(databaseId)}/denials/${userId}`)
      : api.delete(`${base(databaseId)}/denials/${userId}`),
  );
}

export function useSetDatabaseExceptions(databaseId: string) {
  return useAccessMutation(
    databaseId,
    ({ userId, granted, revoked }: { userId: string; granted: string[]; revoked: string[] }) =>
      api.put<DatabaseAccess>(`${base(databaseId)}/members/${userId}/exceptions`, {
        granted,
        revoked,
      }),
  );
}

export function useCreateDatabaseRule(databaseId: string) {
  return useAccessMutation(databaseId, (rule: RuleInput) =>
    api.post<DatabaseAccess>(`${base(databaseId)}/access-rules`, rule),
  );
}

export function useDeleteDatabaseRule(databaseId: string) {
  return useAccessMutation(databaseId, (ruleId: string) =>
    api.delete(`${base(databaseId)}/access-rules/${ruleId}`),
  );
}

export function useResumeDatabaseRule(databaseId: string) {
  return useAccessMutation(databaseId, (ruleId: string) =>
    api.post<DatabaseAccess>(`${base(databaseId)}/access-rules/${ruleId}/resume`),
  );
}

export function useSetDatabaseRolePermissions(databaseId: string) {
  return useAccessMutation(
    databaseId,
    ({ role, permissions }: { role: DatabaseRole; permissions: string[] }) =>
      api.put<DatabaseAccess>(`${base(databaseId)}/role-permissions/${role}`, { permissions }),
  );
}

/** Records of EVERY table of the database that contain `search` (server-side, paged). */
export function useDatabaseRecordSearch(databaseId: string, search: string) {
  const text = search.trim();
  return useInfiniteQuery({
    enabled: text.length > 0,
    queryKey: [...queryKeys.databases.all, 'record-search', databaseId, text] as const,
    queryFn: ({ pageParam, signal }) =>
      api.get<RecordSearchPage>(`/v1/databases/${databaseId}/record-search`, {
        signal,
        query: { q: text, limit: 25, cursor: pageParam },
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
  });
}
